// The start screen (design §14.1): documents newest first, with the search
// field and the + for a new one floating in a pill clear of the bottom edge.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { type DocumentSummary, api } from "../api/client";
import { loadLibrary, saveLibrary } from "../storage/cache";
import { IconButton, TopBar } from "../ui/TopBar";

export function Library() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<DocumentSummary[] | null>(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [stale, setStale] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // This device first, so the library appears at once and appears at all
    // with no connection; the server's list replaces it when it arrives.
    void loadLibrary().then((cached) => {
      if (!cancelled && cached) setDocuments((current) => current ?? cached);
    });
    api
      .get<DocumentSummary[]>("/api/documents")
      .then((list) => {
        if (cancelled) return;
        setDocuments(list);
        setStale(false);
        void saveLibrary(list);
      })
      .catch(() => {
        if (cancelled) return;
        setStale(true);
        setDocuments((current) => {
          if (current === null) setError("Could not load your documents.");
          return current;
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const shown = useMemo(() => {
    if (!documents) return [];
    const q = query.trim().toLowerCase();
    if (!q) return documents;
    return documents.filter(
      (d) =>
        d.title.toLowerCase().includes(q) ||
        d.preview.toLowerCase().includes(q) ||
        d.subject.toLowerCase().includes(q),
    );
  }, [documents, query]);

  const create = async () => {
    setCreating(true);
    try {
      const created = await api.post<{ id: string }>("/api/documents", { title: "", text: "" });
      navigate(`/d/${created.id}`);
    } catch {
      // A new document needs an id from the server, so this is the one thing
      // that genuinely cannot happen offline.
      setError("A new document needs a connection. Existing ones still open.");
      setCreating(false);
    }
  };

  return (
    <div className="screen library">
      <TopBar />

      <main className="body library-body">
        {error && <p className="error">{error}</p>}
        {stale && documents !== null && (
          <p className="muted small">Offline — showing the copy on this device.</p>
        )}
        {documents === null && !error && <p className="muted">Loading…</p>}
        {documents !== null && documents.length === 0 && (
          <p className="muted">Nothing here yet. Press + to start writing.</p>
        )}
        {documents !== null && documents.length > 0 && shown.length === 0 && (
          <p className="muted">Nothing matches “{query}”.</p>
        )}
        <ul className="cards">
          {shown.map((d) => (
            <li key={d.id}>
              <Link to={`/d/${d.id}`} className="card">
                <span className="card-title">{d.title || "Untitled"}</span>
                <span className="card-date">{formatDate(d.updated_at)}</span>
                <span className="card-preview">{d.preview || "Empty"}</span>
                {d.subject && <span className="card-subject">{d.subject}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </main>

      <div className="dock" style={{ bottom: "var(--dock-gap)" }}>
        <div className="pill">
          <div className="pill-row">
            <input
              type="search"
              className="search"
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search documents"
            />
            <IconButton
              icon="plus"
              label="New document"
              primary
              disabled={creating}
              onClick={() => void create()}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
